import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Product carousel: pages through the visible slides of a scroll-snapping viewport. Touch swipe is native scrolling;
 * this component adds previous/next buttons, one dot per page and arrow-key paging. Slides hidden by CSS (fewer
 * products on mobile) are ignored, so arrows and dots always match what is on screen.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The scrolling element.
 * @property {HTMLElement[]} slides - The slides.
 * @property {HTMLButtonElement} previous - The previous button.
 * @property {HTMLButtonElement} next - The next button.
 * @property {HTMLElement} dots - The container for the page dots.
 *
 * @extends Component<Refs>
 */
class DfpProductCarousel extends Component {
  requiredRefs = ['viewport', 'previous', 'next', 'dots'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #frame = 0;
  #pageCount = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /** Goes to the previous page. */
  previous() {
    this.goToPage(this.#currentPage() - 1);
  }

  /** Goes to the next page. */
  next() {
    this.goToPage(this.#currentPage() + 1);
  }

  /**
   * Scrolls so that the first slide of the given page is at the start of the viewport.
   * @param {number} page - Zero-based page index.
   */
  goToPage(page) {
    const slides = this.#visibleSlides();
    const { viewport } = this.refs;
    if (slides.length === 0) return;

    const target = Math.min(Math.max(page, 0), this.#pageCount - 1);
    const slide = slides[Math.min(target * this.#perView(), slides.length - 1)];
    const distance = Math.min(Math.abs(slide.offsetLeft - slides[0].offsetLeft), viewport.scrollWidth - viewport.clientWidth);
    // In right-to-left pages scrollLeft runs from 0 down to a negative value.
    const left = getComputedStyle(viewport).direction === 'rtl' ? -distance : distance;
    viewport.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  #listen() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { viewport } = this.refs;

    viewport.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    viewport.addEventListener('keydown', this.#handleKeydown, { signal });

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#render());
    this.#resizeObserver.observe(viewport);

    this.#render();
  }

  /** Slides that are displayed at the current breakpoint. */
  #visibleSlides() {
    return (this.refs.slides ?? []).filter((slide) => slide.getClientRects().length > 0);
  }

  #perView() {
    const slides = this.#visibleSlides();
    const first = slides[0];
    const second = slides[1];
    if (!first || !second) return 1;
    const step = Math.abs(second.offsetLeft - first.offsetLeft);
    if (step <= 0) return 1;
    return Math.max(1, Math.round((this.refs.viewport.clientWidth + step - first.offsetWidth) / step));
  }

  #currentPage() {
    const { viewport } = this.refs;
    const slides = this.#visibleSlides();
    const first = slides[0];
    const second = slides[1];
    if (!first || !second || this.#pageCount <= 1) return 0;
    const position = Math.abs(viewport.scrollLeft);
    if (position >= viewport.scrollWidth - viewport.clientWidth - 2) return this.#pageCount - 1;
    const step = Math.abs(second.offsetLeft - first.offsetLeft);
    return Math.min(this.#pageCount - 1, Math.round(position / (step * this.#perView())));
  }

  /** Rebuilds the dots when the page count changes (breakpoint or slide count), then updates the state. */
  #render() {
    const pageCount = Math.max(1, Math.ceil(this.#visibleSlides().length / this.#perView()));
    if (pageCount !== this.#pageCount) {
      this.#pageCount = pageCount;
      const { dots } = this.refs;
      const label = this.dataset.dotLabel ?? '';
      dots.replaceChildren();
      if (pageCount > 1) {
        for (let index = 0; index < pageCount; index++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'dfp-carousel__dot';
          dot.setAttribute('aria-label', label.replace('[index]', String(index + 1)).replace('[length]', String(pageCount)));
          dot.addEventListener('click', () => this.goToPage(index), { signal: this.#abortController?.signal });
          dots.append(dot);
        }
      }
      this.refs.previous.hidden = pageCount <= 1;
      this.refs.next.hidden = pageCount <= 1;
    }
    this.#update();
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const page = this.#currentPage();
    this.refs.previous.disabled = page === 0;
    this.refs.next.disabled = page >= this.#pageCount - 1;
    for (const [index, dot] of [...this.refs.dots.children].entries()) {
      dot.setAttribute('aria-current', String(index === page));
    }
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.target !== this.refs.viewport) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  };
}

if (!customElements.get('dfp-product-carousel')) {
  customElements.define('dfp-product-carousel', DfpProductCarousel);
}
